import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import { styled } from 'styled-components';

import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CurrentThresholdStreakHeader } from '#src/components/missions/Streaks/CurrentThresholdStreakHeader/index.tsx';
import streaksBackground from '#src/components/missions/TaskCampaignBanner/assets/bg.webp';
import { TaskResultsPreClaimStoryTemplate } from '#src/components/missions/stories/TaskResultsPreClaimStoryTemplate/index.tsx';

import type {
  ClaimYourStreakRewardsStory_taskInterface,
  ClaimYourStreakRewardsStory_thresholdsStreakTask,
} from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ center: true })`
  position: relative;
  background-image: linear-gradient(
      to bottom,
      var(--c-nl-600) 0%,
      var(--c-nl-900) 70%
    ),
    url(${streaksBackground});
  background-size: cover;
  background-position: center;
  height: 100%;
  width: 100%;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-height: min(calc(90 * var(--unit)), 100vh);
  }
`;

const ButtonsWrapper = styled(Vertical).attrs({ center: true })`
  position: absolute;
  bottom: var(--quadruple-unit);
  left: var(--double-unit);
  right: var(--double-unit);

  opacity: 0;
  animation: ${fadeIn} 0.65s cubic-bezier(0.75, 0.07, 0.23, 0.89) forwards;
  animation-delay: 1.35s;
`;

type Props = {
  task: ClaimYourStreakRewardsStory_thresholdsStreakTask;

  onContinue: () => void;
};

export const ClaimYourStreakRewardsStory = ({ task, onContinue }: Props) => {
  const { progress, so5LeagueTrack } = task;

  return (
    <Root
      key="thresholds-streak-task-story"
      style={
        {
          '--card-avatar-size': 'calc(var(--unit) * 8)',
          '--rgb-color': 'var(--c-rgb-green-400)',
          '--opacity': '0.4',
        } as CSSProperties
      }
    >
      <TaskResultsPreClaimStoryTemplate
        featuredContent={
          <CurrentThresholdStreakHeader
            progress={progress + 1}
            LabelComponent={LabelL}
          />
        }
        title={
          <FormattedMessage
            id="missions.ThresholdsStreakTask.winTitle"
            defaultMessage="You kept the streak alive!"
          />
        }
        subtitle={
          <FormattedMessage
            id="missions.ThresholdsStreakTask.winSubtitle"
            defaultMessage="Congrats! Your {leagueName} streak continues"
            values={{
              leagueName:
                so5LeagueTrack?.entrySo5Leaderboard.so5LeaderboardGroup
                  .displayName || '',
            }}
          />
        }
      />
      <ButtonsWrapper>
        <Button
          size="large"
          onClick={onContinue}
          color="quaternary"
          disableDebounce
        >
          <FormattedMessage {...glossary.continue} />
        </Button>
      </ButtonsWrapper>
    </Root>
  );
};

const fragment_thresholdsStreakTask = gql`
  fragment ClaimYourStreakRewardsStory_thresholdsStreakTask on ThresholdsStreakTask {
    id
    progress
    so5LeagueTrack {
      slug
      entrySo5Leaderboard {
        slug
        so5LeaderboardGroup(groupType: COMPETITION) {
          slug
          displayName
        }
      }
    }
  }
` as TypedDocumentNode<ClaimYourStreakRewardsStory_thresholdsStreakTask>;

ClaimYourStreakRewardsStory.fragments = {
  thresholdsStreakTask: fragment_thresholdsStreakTask,
  taskInterface: gql`
    fragment ClaimYourStreakRewardsStory_taskInterface on TaskInterface {
      id
      ... on ThresholdsStreakTask {
        id
        ...ClaimYourStreakRewardsStory_thresholdsStreakTask
      }
    }
    ${fragment_thresholdsStreakTask}
  ` as TypedDocumentNode<ClaimYourStreakRewardsStory_taskInterface>,
};
